import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../api";
import CategoryNav from "../components/CategoryNav";
import ZoomableImage from '../components/ZoomableImage';
import HomeCategoryProducts from '../components/HomeCategoryProducts';
import "../styles/seo-page.css";

function buildFaqSchema(page) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (Array.isArray(page?.faq) ? page.faq : []).map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

const highlightText = (text, boldKeywords = [], linkedKeywords = []) => {
  if (!text) return "";
  if (typeof text !== "string") return text;

  const bK = Array.isArray(boldKeywords) ? boldKeywords : [];
  const lK = Array.isArray(linkedKeywords) ? linkedKeywords : [];

  // Normalize literal \n or \r\n
  const normalizedText = text.replace(/\\r\\n/gi, '\n').replace(/\\n/gi, '\n').replace(/\\r/gi, '\n');

  const allKeywords = [...new Set([...bK, ...lK.map(item => item.keyword)])].sort((a, b) => b.length - a.length);

  if (allKeywords.length === 0) {
    const lines = normalizedText.split(/(?:\r\n|\r|\n|<br\s*\/?>)/i);
    return lines.map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  }

  const regexPattern = new RegExp(`(${allKeywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const parts = normalizedText.split(regexPattern);
  const result = [];

  parts.forEach((part, i) => {
    if (!part) return;

    const partLower = part.toLowerCase();
    const linkedItem = lK.find(item => item.keyword.toLowerCase() === partLower);
    const isBold = bK.some(k => k.toLowerCase() === partLower);

    if (linkedItem || isBold) {
      const content = isBold ? <strong>{part}</strong> : part;
      if (linkedItem) {
        result.push(
          <a key={`l-${i}`} href={linkedItem.link} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
            {content}
          </a>
        );
      } else {
        result.push(<strong key={`b-${i}`}>{part}</strong>);
      }
    } else {
      // Handle newlines in regular text parts
      const lines = part.split(/(?:\r\n|\r|\n|<br\s*\/?>)/i);
      lines.forEach((line, lineIdx) => {
        result.push(line);
        if (lineIdx < lines.length - 1) {
          result.push(<br key={`br-${i}-${lineIdx}`} />);
        }
      });
    }
  });

  return result;
};

const renderH1 = (text) => {
  if (!text) return null;
  // Handle both [] for large pink text and "" for new line subtext
  const regex = /(\[.*?\]|".*?")/g;
  const parts = text.split(regex);

  return parts.map((part, i) => {
    if (part.startsWith('[') && part.endsWith(']')) {
      return (
        <React.Fragment key={i}>
          <br />
          <span className="h1-special">{part.slice(1, -1)}</span>
        </React.Fragment>
      );
    }
    if (part.startsWith('"') && part.endsWith('"')) {
      return (
        <React.Fragment key={i}>
          <br />
          <span className="h1-subtitle-new">{part.slice(1, -1)}</span>
        </React.Fragment>
      );
    }
    return part;
  });
};

export default function SeoDynamicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [errorText, setErrorText] = useState("");
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [isSticky, setIsSticky] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({ reviewerName: "", rating: 5, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <i key={i} className={i < Math.round(rating) ? 'fas fa-star' : 'far fa-star'}></i>
    ));
  };

  const fetchReviews = async (id) => {
    try {
      const data = await api.getSeoReviews(id);
      setReviews(data || []);
    } catch (err) {
      console.error("Failed to fetch reviews:", err);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setErrorText("");

        const [seoData, categoryData] = await Promise.all([
          api.getSeoPageBySlug(slug),
          api.getCategories(),
        ]);

        if (!mounted) return;

        const normalizedPage = {
          ...seoData,
          sections: Array.isArray(seoData?.sections) ? seoData.sections : [],
          faq: Array.isArray(seoData?.faq) ? seoData.faq : [],
          relatedImages: Array.isArray(seoData?.relatedImages)
            ? seoData.relatedImages
            : [],
          heroImage:
            seoData?.heroImage ||
            "/images/seo/banner-seo1.jpg",
          heroImageAlt:
            seoData?.heroImageAlt || seoData?.h1 || "SEO Page",
        };

        const rawCategories = Array.isArray(categoryData) ? categoryData : [];
        const parentMap = {};
        rawCategories.forEach(c => { parentMap[c.id] = c; });

        const parents = rawCategories.filter(c => !c.parentId);
        parents.sort((a, b) => {
          if ((a.sortOrder || 0) !== (b.sortOrder || 0)) {
            return (a.sortOrder || 0) - (b.sortOrder || 0);
          }
          return (a.name || '').localeCompare(b.name || '', 'vi');
        });

        const orderedList = [];
        parents.forEach(parent => {
          if (parent.showOnPage !== false) {
            orderedList.push({
              ...parent,
              thumbnail: parent.relatedImage || parent.thumbnail || ""
            });
          }

          const children = rawCategories.filter(c => c.parentId === parent.id);
          children.sort((a, b) => {
            if ((a.sortOrder || 0) !== (b.sortOrder || 0)) {
              return (a.sortOrder || 0) - (b.sortOrder || 0);
            }
            return (a.name || '').localeCompare(b.name || '', 'vi');
          });

          children.forEach(child => {
            if (child.showOnPage !== false) {
              orderedList.push({
                ...child,
                thumbnail:
                  child.relatedImage ||
                  child.thumbnail ||
                  parent.relatedImage ||
                  parent.thumbnail ||
                  ""
              });
            }
          });
        });

        // Add any orphaned visible categories
        const addedIds = new Set(orderedList.map(c => c.id));
        rawCategories.forEach(c => {
          if (!addedIds.has(c.id) && c.showOnPage !== false) {
            orderedList.push({
              ...c,
              thumbnail:
                c.relatedImage ||
                c.thumbnail ||
                (c.parentId && parentMap[c.parentId] ? (parentMap[c.parentId].relatedImage || parentMap[c.parentId].thumbnail) : "") ||
                ""
            });
          }
        });

        setPage(normalizedPage);
        setCategories(orderedList);
        fetchReviews(normalizedPage.id);
        window.currentTrackTarget = { type: 'seo_page', id: normalizedPage.id };
        api.trackEvent('seo_page', normalizedPage.id, 'view');
      } catch (error) {
        console.error("Failed to load SEO page:", error);
        setPage(null);
        setErrorText(error.message || "Không tải được trang SEO");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      mounted = false;
      window.currentTrackTarget = null;
    };
  }, [slug]);

  const relatedProducts = useMemo(() => {
    if (!page) return [];

    return categories.map((cat) => {
      const customImage = page.relatedImages.find(
        (item) =>
          item?.categorySlug === cat.slug ||
          Number(item?.categoryId) === Number(cat.id)
      )?.image;

      return {
        name: cat.name,
        path: `/products?category=${cat.slug}`,
        image: customImage || cat.relatedImage || cat.thumbnail || "",
      };
    });
  }, [page, categories]);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewForm.reviewerName || !reviewForm.comment) {
      alert("Vui lòng nhập đầy đủ thông tin");
      return;
    }
    try {
      setSubmittingReview(true);
      await api.submitSeoReview({
        seoPageId: page.id,
        ...reviewForm
      });
      alert("Cảm ơn bạn đã gửi đánh giá! Đánh giá của bạn sẽ được hiển thị sau khi được phê duyệt.");
      setShowReviewModal(false);
      setReviewForm({ reviewerName: "", rating: 5, comment: "" });
    } catch (err) {
      console.error("Failed to submit review:", err);
      alert("Có lỗi xảy ra khi gửi đánh giá. Vui lòng thử lại sau.");
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <main className="seo-page">
        <section className="seo-section">
          <div className="container">
            <div className="seo-content-card" style={{ textAlign: "center" }}>
              Đang tải trang...
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (!page) {
    return (
      <main className="seo-page">
        <section className="seo-section">
          <div className="container">
            <div className="seo-content-card" style={{ textAlign: "center" }}>
              <h1>Không tìm thấy trang SEO</h1>
              <p>{errorText}</p>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const faqSchema = buildFaqSchema(page);

  return (
    <>
      <Helmet>
        <title>{page.metaTitle || page.h1}</title>
        <meta
          name="description"
          content={page.metaDescription || page.intro || ""}
        />
        <meta property="og:title" content={page.metaTitle || page.h1} />
        <meta
          property="og:description"
          content={page.metaDescription || page.intro || ""}
        />
        <meta property="og:image" content={page.heroImage} />
        <meta property="og:type" content="article" />
        <link
          rel="canonical"
          href={`https://hoathangnam.com/khu-vuc/${page.slug}`}
        />
        {page.faq?.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
      </Helmet>

      <main className="seo-page">
        <section className="seo-hero">
          <div className="seo-hero-overlay" />
          <div className="container seo-hero-content">
            <div className="seo-breadcrumb">
              <Link to="/">Trang chủ</Link>
              <span>/</span>
              <Link to="/khu-vuc">Khu vực</Link>
              <span>/</span>
              <span>{page.h1}</span>
            </div>

            <div className="seo-hero-grid">
              <div className="seo-hero-left-col">
                <div className="seo-hero-text">
                  <h1>{renderH1(page.h1)}</h1>
                  <div className="seo-hero-highlights">
                    <div className="highlight-item">
                      <div className="highlight-icon icon-pink">
                        <i className="fas fa-motorcycle"></i>
                      </div>
                      <span>Giao nhanh 60 phút</span>
                    </div>
                    <div className="highlight-item">
                      <div className="highlight-icon icon-green">
                        <i className="fas fa-camera"></i>
                      </div>
                      <span>Gửi ảnh trước khi giao</span>
                    </div>
                    <div className="highlight-item">
                      <div className="highlight-icon icon-pink">
                        <i className="fas fa-fan"></i>
                      </div>
                      <span>Hoa tươi mỗi ngày</span>
                    </div>
                  </div>
                </div>

                <div className="seo-hero-actions-new">
                  <a
                    href="tel:0392621101"
                    className="action-btn btn-call"
                    onClick={() => {
                      api.trackEvent('seo_page', page.id, 'phone');
                      if (window.gtag_report_conversion) window.gtag_report_conversion('tel:0392621101');
                    }}
                  >
                    <div className="btn-icon-wrap">
                      <i className="fas fa-phone-alt"></i>
                    </div>
                    <div className="btn-text">
                      <span className="btn-label">GỌI NGAY</span>
                      <span className="btn-subtext">0392 621 101</span>
                    </div>
                  </a>

                  <a
                    href="https://zalo.me/0392621101"
                    className="action-btn btn-zalo"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      api.trackEvent('seo_page', page.id, 'zalo');
                      if (window.gtag_report_conversion) window.gtag_report_conversion('https://zalo.me/0392621101');
                    }}
                  >
                    <div className="btn-icon-wrap">
                      <img src="/images/zalo.svg" alt="Zalo" className="zalo-icon-main" />
                    </div>
                    <div className="btn-text">
                      <span className="btn-label">CHAT ZALO ĐẶT HOA</span>
                      <span className="btn-subtext">Tư vấn mẫu phù hợp</span>
                    </div>
                  </a>
                </div>

                <div className="seo-urgent-bar">
                  <span>⚡ Nhận đơn gấp - Hỗ trợ 24/7 tất cả các ngày</span>
                </div>
              </div>

              <div className="seo-hero-image-wrap">
                <a href="https://zalo.me/0392621101" target="_blank" rel="noopener noreferrer" className="hero-link-wrap">
                  <img
                    src={page.heroImage}
                    alt={page.heroImageAlt}
                    className="seo-hero-image"
                  />
                </a>
              </div>
            </div>
          </div>
        </section>

        <CategoryNav hiddenCategories={page?.hiddenCategories || []} />

        <section className="seo-why">
          <div className="container">
            <div className="seo-why-box">
              <h2>Tại Sao Nên Đặt Hoa Tại Hoa Tháng Năm?</h2>

              <div className="seo-why-grid">
                <ul>
                  <li>
                    ✅ Ưu đãi hấp dẫn khi đặt hoa online
                  </li>
                  <li>
                    ✅ Miễn phí giao hoa nội thành nhanh chóng
                  </li>
                  <li>
                    ✅ Giao hoa hỏa tốc chỉ trong 60 phút
                  </li>
                </ul>

                <ul>
                  <li>
                    ✅ Hoa tươi tuyển chọn mỗi ngày
                  </li>
                  <li>
                    ✅ Gửi ảnh sản phẩm thực tế trước khi giao
                  </li>
                  <li>
                    ✅ Thiết kế hoa theo yêu cầu cá nhân
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <HomeCategoryProducts categories={categories} hiddenCategories={page?.hiddenCategories || []} zaloLink="https://zalo.me/0392621101" />

        <section className="seo-section seo-light">
          <div className="container">
            <div className="seo-intro-below">
              <div className="seo-intro-description" style={{ whiteSpace: 'pre-wrap' }}>
                {highlightText(page.intro, page?.boldKeywords, page?.linkedKeywords)}
              </div>
            </div>
          </div>
        </section>

        <section className="seo-section">
          <div className="container">
            <div className="seo-content-card">
              {page.sections.map((section, index) => (
                <article key={index} className="seo-article-block">
                  {section.heading && <h2>{highlightText(section.heading, page?.boldKeywords, page?.linkedKeywords)}</h2>}
                  {section.content && <p>{highlightText(section.content, page?.boldKeywords, page?.linkedKeywords)}</p>}
                  {section.image && (
                    <img
                      src={section.image}
                      alt={section.heading || page.h1}
                      className="seo-section-image"
                      style={{ marginTop: "16px" }}
                    />
                  )}
                  {section.children && section.children.map((child, childIndex) => (
                    <div key={childIndex} className="seo-h3-block" style={{ marginTop: "24px" }}>
                      {child.heading && <h3 style={{ fontSize: "1.25rem", marginBottom: "12px", color: "var(--text-color)" }}>{highlightText(child.heading, page?.boldKeywords, page?.linkedKeywords)}</h3>}
                      {child.content && <p>{highlightText(child.content, page?.boldKeywords, page?.linkedKeywords)}</p>}
                      {child.image && (
                        <img
                          src={child.image}
                          alt={child.heading || "H3 Image"}
                          className="seo-section-image"
                          style={{ marginTop: "12px" }}
                        />
                      )}
                    </div>
                  ))}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="seo-section seo-light">
          <div className="container">
            <div className="seo-section-head">
              <h2>Câu hỏi thường gặp</h2>
            </div>

            <div className="seo-faq">
              {(page.faq?.length > 0 ? page.faq : [
                {
                  question: 'Cách thức đặt hoa tại Hoa Tháng Năm?',
                  answer: 'Quý khách hàng có thể sử dụng dịch vụ điện hoa bằng các hình thức: Đặt hoa trực tiếp trên Website, liên hệ qua tổng đài: 039 262 1101, Zalo: 039 262 1101 hoặc Fanpage Facebook.'
                },
                {
                  question: 'Thời gian đặt và giao hoa?',
                  answer: 'Khuyến khích bạn nên đặt hoa trước ít nhất 24h. Trong trường hợp cần giao gấp, chúng tôi vẫn cố gắng hỗ trợ tùy theo thời điểm. Hoa Tháng Năm luôn nỗ lực giao hoa đúng địa điểm và thời gian yêu cầu.'
                },
                {
                  question: 'Có cam kết sẽ giao hoa đúng mẫu không?',
                  answer: 'Thành phẩm thực tế sẽ đạt khoảng 80–90% so với mẫu. Mỗi thiết kế làm thủ công, tuỳ theo mùa thợ sẽ linh động thay đổi hoa phụ và đảm bảo kiểu dáng hài hòa. Chúng tôi sẽ gửi ảnh cho bạn trước khi giao.'
                },
                {
                  question: 'Giao hoa có tính phí vận chuyển không?',
                  answer: 'Hoa Tháng Năm hỗ trợ vận chuyển miễn phí khu vực nội thành. Đối với khu vực xa vùng trung tâm sẽ có tính thêm phụ thu và được báo giá rõ ràng trước khi xác nhận đơn.'
                }
              ]).map((item, index) => (
                <details key={index} className="seo-faq-item">
                  <summary>{highlightText(item.question, page?.boldKeywords, page?.linkedKeywords)}</summary>
                  <p>{highlightText(item.answer, page?.boldKeywords, page?.linkedKeywords)}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="seo-section">
          <div className="container">
            <div className="seo-section-head">
              <h2>Sản phẩm liên quan</h2>
            </div>

            <div className="seo-product-grid">
              {relatedProducts.map((product) => (
                <Link
                  to={product.path}
                  key={product.path}
                  className="seo-product-card"
                >
                  <img
                    src={product.image || "/images/placeholder.jpg"}
                    alt={product.name}
                  />
                  <div className="seo-product-info">
                    <h3>{product.name}</h3>
                    <span>Xem danh mục</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>


        {lightbox.open && (
          <div className="pd-modal" onClick={() => setLightbox({ open: false, index: 0 })}>
            <div className="pd-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="pd-modal-close" onClick={() => setLightbox({ open: false, index: 0 })}>
                <i className="fas fa-times"></i>
              </button>
              <div className="pd-modal-img">
                <ZoomableImage
                  src={[
                    '/ban_giao_hoa/1.jpg',
                    '/ban_giao_hoa/2.jpg',
                    '/ban_giao_hoa/3.jpg',
                    '/ban_giao_hoa/4.jpg',
                    '/ban_giao_hoa/5.jpg',
                    '/ban_giao_hoa/6.jpg',
                    '/ban_giao_hoa/7.jpg',
                    '/ban_giao_hoa/9.jpg',
                  ][lightbox.index]}
                  alt="Bàn giao hoa"
                  style={{ maxHeight: '85vh', objectFit: 'contain', width: '100%' }}
                />
              </div>
              <div className="pd-modal-nav">
                <button onClick={() => setLightbox(prev => ({ ...prev, index: prev.index === 0 ? 7 : prev.index - 1 }))}>
                  <i className="fas fa-chevron-left"></i>
                </button>
                <span>{lightbox.index + 1} / 8</span>
                <button onClick={() => setLightbox(prev => ({ ...prev, index: (prev.index + 1) % 8 }))}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Floating Buttons on scroll for Mobile */}
        <div className={`seo-mobile-fixed-actions ${isSticky ? 'visible' : ''}`}>
          <div className="seo-mobile-fixed-inner">
            <a
              href="tel:0392621101"
              className="action-btn btn-call"
              onClick={() => {
                api.trackEvent('seo_page', page.id, 'phone');
                if (window.gtag_report_conversion) window.gtag_report_conversion('tel:0392621101');
              }}
            >
              <div className="btn-icon-wrap">
                <i className="fas fa-phone-alt"></i>
              </div>
              <div className="btn-text">
                <span className="btn-label">GỌI NGAY</span>
                <span className="btn-subtext">0392 621 101</span>
              </div>
            </a>

            <a
              href="https://zalo.me/0392621101"
              className="action-btn btn-zalo"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                api.trackEvent('seo_page', page.id, 'zalo');
                if (window.gtag_report_conversion) window.gtag_report_conversion('https://zalo.me/0392621101');
              }}
            >
              <div className="btn-icon-wrap">
                <img src="/images/zalo.svg" alt="Zalo" className="zalo-icon-main" />
              </div>
              <div className="btn-text">
                <span className="btn-label">CHAT ZALO ĐẶT HOA</span>
                <span className="btn-subtext">Tư vấn mẫu phù hợp</span>
              </div>
            </a>
          </div>
        </div>

        <section className="seo-reviews-section">
          <div className="container">
            <div className="seo-reviews-header">
              <h2>Đánh giá của khách hàng</h2>
              <button className="btn-write-review" onClick={() => setShowReviewModal(true)}>
                Nhập đánh giá
              </button>
            </div>

            {reviews.length > 0 && (
              <div className="pd-reviews-summary" style={{ marginBottom: '30px' }}>
                <div className="pd-reviews-score">
                  <span className="pd-reviews-number">
                    {(reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)}
                  </span>
                  <div className="pd-reviews-stars">
                    {renderStars(reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length)}
                  </div>
                  <span className="pd-reviews-count">{reviews.length} đánh giá</span>
                </div>

                <div className="pd-reviews-bars">
                  {[5, 4, 3, 2, 1].map(star => {
                    const count = reviews.filter(r => Number(r.rating) === star).length;
                    const pct = reviews.length > 0 ? (count / reviews.length * 100) : 0;
                    return (
                      <div key={star} className="pd-review-bar">
                        <span>{star} <i className="fas fa-star"></i></span>
                        <div className="pd-review-bar-track">
                          <div
                            className="pd-review-bar-fill"
                            style={{ width: `${pct}%` }}
                          ></div>
                        </div>
                        <span className="pd-review-bar-count">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="seo-reviews-grid">
              {reviews.length > 0 ? (
                reviews.map((review) => (
                  <div key={review.id} className="seo-review-card">
                    <div className="seo-review-stars">
                      {renderStars(review.rating)}
                    </div>
                    <div className="seo-reviewer-name">{review.reviewerName}</div>
                    <div className="seo-review-comment">{review.comment}</div>
                    {review.reply && (
                      <div className="seo-review-reply">
                        <span className="seo-review-reply-label">Hoa Tháng Năm trả lời:</span>
                        {review.reply}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p>Chưa có đánh giá nào cho trang này.</p>
              )}
            </div>
          </div>
        </section>

        <section className="delivery-section">
          <div className="container">
            <div className="section-header">
              <span className="section-subtitle">Hình ảnh thực tế</span>
              <h2 className="section-title">
                Một số hình ảnh bàn giao hoa cho khách
              </h2>
            </div>

            <div className="delivery-grid">
              {[
                '/ban_giao_hoa/1.jpg',
                '/ban_giao_hoa/2.jpg',
                '/ban_giao_hoa/3.jpg',
                '/ban_giao_hoa/4.jpg',
                '/ban_giao_hoa/5.jpg',
                '/ban_giao_hoa/6.jpg',
                '/ban_giao_hoa/7.jpg',
                '/ban_giao_hoa/9.jpg',
              ].map((img, index) => (
                <div key={index} className="delivery-item">
                  <img src={img} alt="Bàn giao hoa" onClick={() => setLightbox({ open: true, index })} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {showReviewModal && (
          <div className="seo-review-modal-overlay" onClick={() => setShowReviewModal(false)}>
            <div className="seo-review-modal" onClick={(e) => e.stopPropagation()}>
              <button className="seo-review-modal-close" onClick={() => setShowReviewModal(false)}>
                <i className="fas fa-times"></i>
              </button>
              <div className="seo-review-modal-content">
                <h3>Nhập đánh giá</h3>
                <form onSubmit={handleReviewSubmit}>
                  <div className="seo-form-group">
                    <label>Đánh giá của bạn</label>
                    <div className="seo-star-rating">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`fas fa-star ${reviewForm.rating >= star ? 'active' : ''}`}
                          onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <div className="seo-form-group">
                    <label>Họ tên</label>
                    <input
                      type="text"
                      className="seo-form-input"
                      value={reviewForm.reviewerName}
                      onChange={(e) => setReviewForm({ ...reviewForm, reviewerName: e.target.value })}
                      placeholder="Nhập tên của bạn"
                      required
                    />
                  </div>
                  <div className="seo-form-group">
                    <label>Nội dung đánh giá</label>
                    <textarea
                      className="seo-form-textarea"
                      value={reviewForm.comment}
                      onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                      placeholder="Chia sẻ trải nghiệm của bạn"
                      required
                    ></textarea>
                  </div>
                  <button type="submit" className="btn-submit-review" disabled={submittingReview}>
                    {submittingReview ? "Đang gửi..." : "Gửi đánh giá"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Hide global social float when sticky is active on SEO page */}
      <style>
        {`
          ${isSticky ? `
            @media (max-width: 768px) {
              .social-float {
                display: none !important;
              }
            }
          ` : ''}
        `}
      </style>
    </>
  );
}
